import { Chip, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

interface RecipientChipsProps {
  emails: Array<{ email: string }>;
  slacks: Array<{ slackName: string }>;
  groups: Array<{ groupId: string, groupName: string }>;
}

const RecipientChips = ({ emails, slacks, groups }: RecipientChipsProps) => {
  const { t } = useTranslation();
  
  const getTooltipContent = () => {
      const content = [];
      
      if (emails.length > 0) {
          content.push(
              <div key="emails" className="mb-2">
                  <div className="font-semibold">{t('emails')}:</div>
                  {emails.map((email, index) => (
                      <div key={`email-tooltip-${index}`} className="ml-2">• {email.email}</div>
                  ))}
              </div>
          );
      }
      
      if (slacks.length > 0) {
          content.push(
              <div key="slacks" className="mb-2">
                  <div className="font-semibold">{t('slacks')}:</div>
                  {slacks.map((slack, index) => (
                      <div key={`slack-tooltip-${index}`} className="ml-2">• {slack.slackName}</div>
                  ))}
              </div>
          );
      }
      
      if (groups.length > 0) {
          content.push(
              <div key="groups" className="mb-2">
                  <div className="font-semibold">{t('groups')}:</div>
                  {groups.map((group, index) => (
                      <div key={`group-tooltip-${index}`} className="ml-2">• {group.groupName}</div>
                  ))}
              </div>
          );
      }
      
      return content;
  };

  return (
      <Tooltip 
          title={
              <div className="p-2">
                  {getTooltipContent()}
              </div>
          }
          arrow
      >
          <div className="flex flex-wrap gap-2 items-center h-full">
              {emails.length > 0 && (
                  <div className="flex gap-1">
                      {emails.slice(0, 1).map((email, index) => (
                          <Chip key={`email-${index}`} label={email.email} size="small" className="bg-blue-100 text-blue-800" />
                      ))}
                      {emails.length > 1 && (
                          <Chip 
                              key="email-more" 
                              label={`+${emails.length - 1} ${t('more')}`} 
                              size="small" 
                              className="bg-blue-100 text-blue-800"
                          />
                      )}
                  </div>
              )}
              {slacks.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                      {slacks.slice(0, 2).map((slack, index) => (
                          <Chip key={`slack-${index}`} label={slack.slackName} size="small" className="bg-purple-100 text-purple-800" />
                      ))}
                      {slacks.length > 2 && (
                          <Chip 
                              key="slack-more" 
                              label={`+${slacks.length - 2} ${t('more')}`} 
                              size="small" 
                              className="bg-purple-100 text-purple-800"
                          />
                      )}
                  </div>
              )}
              {groups.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                      {groups.slice(0, 2).map((group, index) => (
                          <Chip key={`group-${index}`} label={group.groupName} size="small" className="bg-green-100 text-green-800" />
                      ))}
                      {groups.length > 2 && (
                          <Chip 
                              key="group-more" 
                              label={`+${groups.length - 2} ${t('more')}`} 
                              size="small" 
                              className="bg-green-100 text-green-800"
                          />
                      )}
                  </div>
              )}
          </div>
      </Tooltip>
  );
};

export default RecipientChips;